import React, { ReactElement, useEffect, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Error, NotFound, SplashRenderer, Icon } from '@shootsta/common-react';

import { getAnalytics, setAnalytics } from '../../../utils';

import AnalyticsTypes from '../../../constants/AnalyticsTypes';

import { Contents, GraphqlResponse } from '../../../types';

interface SplashPageProps {
  getSplashPage: () => Promise<GraphqlResponse>;
  createAnalyticsEntry: (type: string) => Promise<GraphqlResponse>;
  getPublicAsset: (assetId: string) => Promise<GraphqlResponse>;
  splashPageId: string;
}

interface SplashDetailsState {
  isLoading: boolean;
  data: unknown | undefined;
  error: Error | null
}

interface AssetDetailsState {
  isLoading: boolean;
  data: unknown | undefined;
  error: Error | null
}

const SplashPage = (props: SplashPageProps): ReactElement | null => {
  const { getSplashPage, createAnalyticsEntry, getPublicAsset, splashPageId } = props;

  const [splashDetails, setSplashDetails] = useState<SplashDetailsState>({
    isLoading: false,
    data: void 0,
    error: null
  });

  const [assetDetails, setAssetDetails] = useState<AssetDetailsState>({
    isLoading: false,
    data: void 0,
    error: null
  });

  const fetchSplashData = useCallback(async () => {
    setSplashDetails((state: SplashDetailsState) => ({ ...state, isLoading: true }));
    const { data, error } = await getSplashPage();

    if (!error) {
      setSplashDetails((state: SplashDetailsState) => ({ ...state, data: data?.getShareablePage }));
    } else {
      setSplashDetails((state: SplashDetailsState) => ({ ...state, error }));
    }

    setSplashDetails((state: SplashDetailsState) => ({ ...state, isLoading: false }));
  }, [getSplashPage, setSplashDetails]);

  useEffect(() => {
    fetchSplashData();
  }, [fetchSplashData]);

  const fetchPublicAsset = useCallback(
    async (assetId: string): Promise<void> => {
      setAssetDetails((state) => ({ ...state, isLoading: true }));
      const {
        data: { getPublicAsset: publicAsset } = {},
        error: err
      } = await getPublicAsset(assetId);

      if (err) {
        setAssetDetails((state) => ({ ...state, isLoading: false }));
      } else {
        setAssetDetails((state) => ({ ...state, data: publicAsset }));
      }

      setAssetDetails((state) => ({ ...state, isLoading: false }));
    },
    [getPublicAsset, setAssetDetails]
  );

  const handlePageViewAnalytics = useCallback(async () => {
    const {
      error: analyticsError,
      data: analyticsData
    } = await createAnalyticsEntry(AnalyticsTypes.PAGE_VIEW);

    if (analyticsError || !analyticsData?.createAnalyticsEntry) {
      return void 0;
    }

    setAnalytics(AnalyticsTypes.PAGE_VIEW);
  }, [createAnalyticsEntry]);

  useEffect(() => {
    const isPageViewAnalyticsSent = JSON.parse(getAnalytics(AnalyticsTypes.PAGE_VIEW) || 'false');

    if (splashDetails.data && !isPageViewAnalyticsSent) {
      handlePageViewAnalytics();
    }
  }, [createAnalyticsEntry, handlePageViewAnalytics, splashDetails.data]);

  const onButtonClick = useCallback(async (buttonProperties) => {
    const { buttonLink } = buttonProperties;

    if (buttonLink) {
      window.open(buttonLink);
    }

    const ctaClickAnalytics = JSON.parse(getAnalytics(AnalyticsTypes.CTA_CLICK) || 'false');

    if (!createAnalyticsEntry || ctaClickAnalytics) {
      return void 0;
    }

    const { error, data } = await createAnalyticsEntry(AnalyticsTypes.CTA_CLICK);

    if (error || !data || !data.createAnalyticsEntry) {
      return void 0;
    }

    setAnalytics(AnalyticsTypes.CTA_CLICK);
  }, [createAnalyticsEntry]);

  if (splashDetails.error) {
    return <Error />;
  }

  if (splashDetails.data === null) {
    return <NotFound />;
  }

  if (splashDetails.isLoading) {
    return (
      <main style={{ justifyContent: 'center' }}>
        <Icon name="shootstagrad" size={40} color="black" spin />
      </main>
    );
  }

  const pageTitle: string = (splashDetails.data as { title: string })?.title;
  const contents: Contents = (splashDetails.data as { contents: Contents })?.contents;
  const brandingColor: string = (splashDetails.data as { brandingColor: string })?.brandingColor;
  const allowCalendarLinks: boolean = (splashDetails.data as {
    allowCalendarLinks: boolean
  })?.allowCalendarLinks;

  const enableCaptions: boolean = (
    splashDetails.data as { captionsEnabled: boolean }
    )?.captionsEnabled;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <SplashRenderer
        contents={contents}
        shootstaPlayerProps={{
          assetDetails,
          analyticsResourceId: splashPageId,
          hideDownloadButton: true,
          loadDefaultCaptions: true,
          hideCaptionButton: !enableCaptions,
          createAnalyticsEntry,
          fetchPublicAsset,
          subscribeToAssetTranscoded: () => void 0
        }}
        allowCalendarLinks={allowCalendarLinks}
        brandingColor={brandingColor}
        onButtonClick={onButtonClick}
      />
    </>
  );
};

export default SplashPage;
